import { useCallback, useEffect, useRef, useState } from 'react'
import { runLogAccumulator } from '@qim/shared'
import { fetchRun, fetchRunSummary } from '../api.js'
import { formatInstant } from '../format.js'
import { AccountLink } from './account-link.jsx'
import { RunLog } from './run-log.jsx'
import { RunSummaryCard } from './run-summary.jsx'
import { RunTail } from './run-tail.jsx'
import { RunState } from './status.jsx'
import type { RunLogSummary } from '@qim/shared'
import type { RunSummary, RunSummaryResponse } from '../api.js'
import type { JSX } from 'react'

/**
 * One run, at its own address.
 *
 * ## Why it needs one
 *
 * A run's log used to be a `<details>` inside a `<details>` inside a row of the
 * deployment screen's history table. There was no address for "this run", so it
 * could not be pasted into a ticket or a Slack thread — which is precisely what
 * someone does when a deploy is going wrong.
 *
 * Giving it a path also removes the nesting that caused IM-83: the deployment
 * screen links out rather than mounting a log component for every row and hoping
 * nobody opens them.
 *
 * ## What it leads with
 *
 * For a live run, the tail — someone watching wants to know it is still moving.
 * For a failed one, the extracted cause: `lastFailure` already parses
 * Terraform's diagnostic frame, and the completion event carries nothing but an
 * exit code, so the record's own message is "the installer exited 1" and useless
 * on its own.
 */

/*
 * Nothing here can move these, so nothing polls them. `awaiting-approval` waits
 * on a person for up to a week (IM-102): it is not terminal, but it is still,
 * and polling it every three seconds would read a value a button elsewhere
 * changes. Reloading the screen after the approval shows it started.
 */
const TERMINAL = new Set(['succeeded', 'failed', 'rejected', 'expired', 'awaiting-approval'])

export const Run = ({
  baseUrl,
  tenantId,
  runId,
  initial,
  role,
  onOpen,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly runId: string
  readonly initial: RunSummary
  /**
   * For the account's way to its onboarding screen (IM-181): a run that was
   * refused is most often refused by the account, and that screen says why.
   */
  readonly role: string
  readonly onOpen: (path: string) => void
}): JSX.Element => {
  const [run, setRun] = useState(initial)
  const [showFullLog, setShowFullLog] = useState(false)

  const live = !TERMINAL.has(run.state)

  /*
   * What the log says it did (IM-35, IM-165), two ways.
   *
   * **While the run is going**, from the lines the tail is already fetching:
   * an accumulator holds one entry per resource rather than the log, so this
   * costs no read of its own and no memory that grows with the run. It sees
   * every line, because the tail pages forward from the start.
   *
   * **Once it has finished**, from the API, which reads the whole log once and
   * keeps the answer — so this screen asks for it on mount and every later
   * visit is a read of Qrvey's own table. This screen is opened on purpose,
   * one run at a time, which is what keeps the IM-83 rule: no list and no
   * closed panel ever causes that read.
   */
  const accumulator = useRef(runLogAccumulator())
  const [liveSummary, setLiveSummary] = useState<RunLogSummary | undefined>(undefined)
  const [finished, setFinished] = useState<RunSummaryResponse | undefined>(undefined)
  const [summaryFailed, setSummaryFailed] = useState(false)

  const onLines = useCallback(
    (messages: readonly string[]) => {
      if (!live) return
      accumulator.current.push(messages)
      setLiveSummary(
        accumulator.current.summary({
          complete: false,
          incompleteReason: 'The run is still going.',
        }),
      )
    },
    [live],
  )

  useEffect(() => {
    if (live) return
    let alive = true
    fetchRunSummary(baseUrl, tenantId, runId)
      .then((answer) => {
        if (alive) setFinished(answer)
      })
      .catch(() => {
        if (alive) setSummaryFailed(true)
      })
    return () => {
      alive = false
    }
  }, [baseUrl, tenantId, runId, live])

  /*
   * Follows the record, not the customer's cloud. The completion event already
   * updated it, so this reads a value that is there — and stops as soon as the
   * run is terminal, which is also when the tail stops.
   */
  useEffect(() => {
    setRun(initial)
    if (TERMINAL.has(initial.state)) return

    let alive = true
    const timer = setInterval(() => {
      void fetchRun(baseUrl, tenantId, runId)
        .then((answer) => {
          if (!alive) return
          setRun(answer)
          if (TERMINAL.has(answer.state)) clearInterval(timer)
        })
        .catch(() => undefined)
    }, 3000)

    return () => {
      alive = false
      clearInterval(timer)
    }
  }, [baseUrl, tenantId, runId, initial])

  return (
    <>
      <section className="panel">
        <div className="run-head">
          <h2>
            {run.command} <span className="mono-cell">{run.deploymentId ?? runId}</span>
          </h2>
          <RunState state={run.state} />
        </div>

        <dl className="detail">
          <dt>Run</dt>
          <dd>
            <code>{runId}</code>
          </dd>
          <dt>Cloud account</dt>
          <dd>
            {run.accountKey === undefined ? (
              <code>{run.accountKey}</code>
            ) : (
              <AccountLink
                tenantId={tenantId}
                accountKey={run.accountKey}
                role={role}
                onOpen={onOpen}
                className="fact-link"
              >
                <code>{run.accountKey}</code>
              </AccountLink>
            )}
          </dd>
          {run.region === undefined ? null : (
            <>
              <dt>Region</dt>
              <dd>
                <code>{run.region}</code>
              </dd>
            </>
          )}
          {run.chartVersion === undefined ? null : (
            <>
              <dt>Version</dt>
              <dd>{run.chartVersion}</dd>
            </>
          )}
          <dt>Started</dt>
          <dd>
            <time dateTime={run.requestedAt}>{formatInstant(run.requestedAt)}</time>
          </dd>
          <dt>By</dt>
          <dd>{run.requestedBy}</dd>
          {/* Who had to agree, and who did (IM-102, IM-39). Both people are
            named, because "who asked and who let it through" is one question. */}
          {run.approval === undefined ? null : (
            <>
              <dt>Approval</dt>
              <dd>
                {run.approval.decision?.outcome === 'no-approver'
                  ? // Nobody decided (IM-176): the reason is the whole answer,
                    // and "no-approver by system" would read as a person.
                    (run.approval.decision.reason ??
                    'Went ahead without approval: nobody at the customer could approve.')
                  : run.approval.decision === undefined
                    ? `Waiting for ${run.approval.requirement === 'customer' ? 'the customer' : 'a second approver'}${
                        run.approval.expiresAt === undefined
                          ? ''
                          : ` until ${formatInstant(run.approval.expiresAt)}`
                      }. Nothing has been started.`
                    : `${run.approval.decision.outcome} by ${run.approval.decision.by} on ${formatInstant(run.approval.decision.at)}`}
                {run.approval.decision?.reason === undefined ||
                run.approval.decision.outcome === 'no-approver' ? null : (
                  <p className="muted">{run.approval.decision.reason}</p>
                )}
              </dd>
            </>
          )}
        </dl>
      </section>

      {/* The reason, where the reason belongs. The run record's own message is
        "the installer exited 1" — true and useless — because the completion
        event carries only an exit code. */}
      {run.failure === undefined ? null : (
        <section className="panel">
          <h3>What went wrong</h3>
          <p role="alert">{run.failure.detail}</p>
          <p className="muted">{run.failure.reason}</p>
        </section>
      )}

      <RunSummaryPanel
        live={live}
        liveSummary={liveSummary}
        finished={finished}
        failed={summaryFailed}
      />

      <section className="panel">
        <h3>Output</h3>
        <RunTail
          baseUrl={baseUrl}
          tenantId={tenantId}
          runId={runId}
          live={live}
          onOpenFullLog={() => setShowFullLog(true)}
          onLines={onLines}
        />

        {/* Mounted only once asked for. The whole reason the tail can stay small
          is that the full read is a separate, deliberate act — mounting it here
          by default would be IM-83 again in a new place. */}
        {showFullLog ? (
          <div className="full-log">
            <h4>Full log</h4>
            {/* Keyed by the run, so another run's log never inherits this one's
              folds. `active` follows the run: opened while it is going, the
              full log keeps reading, and the folds survive what it appends. */}
            <RunLog key={runId} baseUrl={baseUrl} tenantId={tenantId} runId={runId} active={live} />
          </div>
        ) : null}
      </section>
    </>
  )
}

/** The summary card's panel, or nothing yet. Above the log, where the answer belongs. */
const RunSummaryPanel = ({
  live,
  liveSummary,
  finished,
  failed,
}: {
  readonly live: boolean
  readonly liveSummary: RunLogSummary | undefined
  readonly finished: RunSummaryResponse | undefined
  readonly failed: boolean
}): JSX.Element | null => {
  if (live) {
    // Nothing until the tail has something that looks like Terraform.
    if (liveSummary === undefined || liveSummary.parsed === 'unparseable') return null
    return (
      <section className="panel">
        <h3>So far</h3>
        <RunSummaryCard summary={liveSummary} live />
      </section>
    )
  }
  if (failed) {
    return (
      <section className="panel">
        <h3>Summary</h3>
        <p className="muted">The summary could not be read just now. The log below is complete.</p>
      </section>
    )
  }
  if (finished === undefined) return null
  return (
    <section className="panel">
      <h3>Summary</h3>
      {finished.summary === null ? (
        <p className="muted">{finished.note}</p>
      ) : (
        <RunSummaryCard summary={finished.summary} />
      )}
    </section>
  )
}
